"use client"

import { useEffect, useState } from "react"
import { getDatabase, ref, get, set } from "firebase/database"
import { Eye } from "lucide-react"
import "./refunds.css";
import InfoIcon from "./infoIcon";

const RefundPage = () => {
  const [refunds, setRefunds] = useState([])
  const [visibleRefunds, setVisibleRefunds] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedRefund, setSelectedRefund] = useState(null)
const gifUrl = "/Images/New notification.gif";

  // ✅ Utility for timestamp in IST
const getIndianTimestamp = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const istTime = new Date(now.getTime() + istOffset);
  return istTime.toISOString().replace("T", " ").replace("Z", "");
};

  // ✅ Function to send notification
const sendNotification = async (userId, refundId, title, message) => {
  const db = getDatabase()
  const notificationRef = ref(db, `user_notifications/Refund Added/${userId}/${refundId}`)

  const newNotification = {
    title,
    message,
    gifUrl, // use global constant
    timestamp: getIndianTimestamp(), // formatted timestamp
    seen: false,
  }

  try {
    await set(notificationRef, newNotification)
    console.log("Notification sent successfully:", newNotification)
  } catch (error) {
    console.error("Error sending notification:", error)
  }
}


  useEffect(() => {
    const fetchRefunds = async () => {
      try {
        const db = getDatabase()
        const snapshot = await get(ref(db, "refunds/"))
        const data = snapshot.val()

        if (!data) {
          setError("No refund requests found.")
          setLoading(false)
          return
        }

        const allRefunds = []
        const promises = Object.entries(data).map(async ([userId, refundsData]) => {
          try {
            const companySnapshot = await get(ref(db, `profileDashboard/${userId}/company_name`))
            const companyName = companySnapshot.val() || "Unknown"

            Object.entries(refundsData).forEach(([refundId, refundDetails]) => {
              if (refundDetails.resolved === true) return

              allRefunds.push({
                userId,
                refundId,
                companyName,
                ...refundDetails,
              })
            })
          } catch (error) {
            console.error(`Error fetching company name for user ${userId}:`, error)
            Object.entries(refundsData).forEach(([refundId, refundDetails]) => {
              if (refundDetails.resolved === true) return

              allRefunds.push({
                userId,
                refundId,
                companyName: "Unknown",
                ...refundDetails,
              })
            })
          }
        })

        await Promise.all(promises)

        allRefunds.sort((a, b) => {
          const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0
          const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0
          return timestampB - timestampA
        })

        setRefunds(allRefunds)
        setVisibleRefunds(allRefunds)
        setLoading(false)

        if (allRefunds.length === 0) {
          setError("No pending refund requests found.")
        }
      } catch (error) {
        console.error("Error fetching refunds:", error)
        setError("Failed to fetch refund data")
        setLoading(false)
      }
    }

    fetchRefunds()
  }, [])

  const openFile = (url) => {
    if (url) {
      window.open(url, "_blank")
    } else {
      alert("File URL not available")
    }
  }

  const handleCheckboxChange = (refund) => {
    setSelectedRefund(refund)
  }

  // ✅ Updated confirm resolve with notification
  const handleConfirmResolve = async () => {
  if (!selectedRefund) return

  try {
    const db = getDatabase()
    const { userId, refundId, companyName, balance } = selectedRefund

    // Mark refund as resolved in Firebase
    await set(ref(db, `refunds/${userId}/${refundId}/resolved`), true)

    // Send notification with refundId as node
    await sendNotification(
      userId,
      refundId,
      "Refund Added ✅",
      `Your refund request of ₹${balance} for ${companyName} has been resolved successfully.`
    )

    // Remove from UI
    setVisibleRefunds((prev) => prev.filter((r) => r.refundId !== refundId))
    setRefunds((prev) => prev.filter((r) => r.refundId !== refundId))
    setSelectedRefund(null)

    alert("Refund request marked as resolved and notification sent successfully!")
  } catch (error) {
    console.error("Error resolving refund:", error)
    alert("Failed to resolve refund request. Please try again.")
  }
}


  const handleCancelModal = () => {
    setSelectedRefund(null)
  }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A"

    try {
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return "Invalid Date"

      return {
        date: date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }),
        time: date.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      }
    } catch (error) {
      return "Invalid Date"
    }
  }
  
  return (
    <div className="refundPage">
      <h2>Refund Requests ({visibleRefunds.length}) <InfoIcon text="The Refund/Issue Management page lists each record with CodeID, Company, Brand, Balance, Issue, and bank details (Account No., IFSC, Beneficiary, Branch). Timestamp and attached Files support tracking, and the Mark as Resolved option closes the issue once addressed." /></h2>

      {visibleRefunds.length === 0 ? (
        <div className="no-data">
          <p>No pending refund requests available.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="refund-table">
            <thead>
              <tr>
                <th>Mark as Resolved</th>
                <th>CodeID</th>
                <th>Company Name</th>
                <th>Brand</th>
                <th>Balance</th>
                <th>Issue</th>
                <th>Account No.</th>
                <th>IFSC</th>
                <th>Beneficiary</th>
                <th>Branch</th>
                <th>Timestamp</th>
                <th>Files</th>
              </tr>
            </thead>
            <tbody>
              {visibleRefunds.map((refund, index) => {
                const formattedTime = formatTimestamp(refund.timestamp)

                return (
                  <tr key={`${refund.userId}-${refund.refundId}`}>
                    <td>
                      <input
                        type="checkbox"
                        onChange={() => handleCheckboxChange(refund)}
                        checked={selectedRefund?.refundId === refund.refundId}
                      />
                    </td>
                    <td>{refund.codeId || "N/A"}</td>
                    <td>{refund.companyName}</td>
                    <td>{refund.brand || "N/A"}</td>
                    <td>₹{refund.balance || "0"}</td>
                    <td>{refund.issue || "N/A"}</td>
                    <td>{refund.selectedBankDetail?.AccountNumber || "N/A"}</td>
                    <td>{refund.selectedBankDetail?.IFSC || "N/A"}</td>
                    <td>{refund.selectedBankDetail?.BeneficiaryName || "N/A"}</td>
                    <td>{refund.selectedBankDetail?.Branch || "N/A"}</td>
                    <td>
                      {typeof formattedTime === "object" ? (
                        <>
                          <div>{formattedTime.date}</div>
                          <div style={{ fontSize: "0.85em", color: "#666" }}>{formattedTime.time}</div>
                        </>
                      ) : (
                        formattedTime
                      )}
                    </td>
                    <td className="file-buttons">
                      {refund.files?.cancel_cheque && (
                        <button
                          onClick={() => openFile(refund.files.cancel_cheque)}
                          className="view-btn"
                          title="View Cancel Cheque"
                        >
                          <Eye size={14} />
                          Cancel Cheque
                        </button>
                      )}
                      {refund.files?.letterhead && (
                        <button
                          onClick={() => openFile(refund.files.letterhead)}
                          className="view-btn"
                          title="View Letterhead"
                        >
                          <Eye size={14} />
                          Letterhead
                        </button>
                      )}
                      {!refund.files?.cancel_cheque && !refund.files?.letterhead && (
                        <span style={{ color: "#999", fontSize: "0.9em" }}>No files</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedRefund && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h3>Confirm Resolution</h3>
            <p>Are you sure you want to mark this refund request as resolved?</p>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              *This will hide the entry from view but retain it in the database.
            </p>
            <div className="refund-details">
              <p>
                <strong>Company:</strong> {selectedRefund.companyName}
              </p>
              <p>
                <strong>Amount:</strong> ₹{selectedRefund.balance}
              </p>
              <p>
                <strong>Issue:</strong> {selectedRefund.issue}
              </p>
            </div>
            <div className="modal-actions">
              <button
                onClick={handleConfirmResolve}
                style={{
                  background: "#4caf50",
                  color: "#fff",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Yes, Resolve
              </button>
              <button
                onClick={handleCancelModal}
                style={{
                  background: "#f44336",
                  color: "#fff",
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RefundPage